import { useMemo, useState } from 'react';
import { addMinutes, format } from 'date-fns';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import {
  useAvailableSlots,
  useServiceSlots,
  useCreateSlot,
  useDeleteSlot,
} from '../hooks/useBookingServices';
import type { AvailableSlot, BookingService } from '../api/bookingServicesApi';

interface Props {
  service: BookingService;
  /** Merchant-wide default buffer (minutes), used when the service has no override. */
  merchantDefaultBufferMins?: number;
}

const WINDOW_DAYS = 14;

/**
 * Does `slot` fall within `bufferMins` of any existing booking/block in `existing`?
 * Buffer is measured from each existing slot's start and end, matching the
 * "gap between consecutive bookings" semantics of booking-service `bufferMins`.
 */
function isBufferBlocked(
  slot: AvailableSlot,
  existing: { start: string; end: string }[],
  bufferMins: number,
): boolean {
  if (bufferMins <= 0) return false;
  const slotStart = new Date(slot.start).getTime();
  const slotEnd = new Date(slot.end).getTime();
  const bufferMs = bufferMins * 60 * 1000;
  return existing.some((e) => {
    const guardStart = new Date(e.start).getTime() - bufferMs;
    const guardEnd = new Date(e.end).getTime() + bufferMs;
    return slotStart < guardEnd && slotEnd > guardStart;
  });
}

/**
 * Slot picker for one bookable service (PE-2137).
 *
 * - CUSTOM_ORDER services get a free-form start/end picker that never fetches
 *   availability and never blocks on existing bookings or buffer — custom-order
 *   timing is deliberately unconstrained per the ticket.
 * - Every other service gets the buffer-aware grid: computed available slots
 *   (GET /bookings/slots) greyed out when they fall inside the buffer window of
 *   an existing booking/block (GET /bookings/services/:id/slots), with a manual
 *   "block" action per open slot (POST/DELETE /bookings/slots) for breaks.
 */
export function BookingSlotPicker({ service, merchantDefaultBufferMins }: Props) {
  if (service.category === 'CUSTOM_ORDER') {
    return <CustomOrderTimingPicker service={service} />;
  }
  return (
    <ServiceSlotGrid
      service={service}
      effectiveBufferMins={service.bufferMins ?? merchantDefaultBufferMins ?? 0}
    />
  );
}

function ServiceSlotGrid({
  service,
  effectiveBufferMins,
}: {
  service: BookingService;
  effectiveBufferMins: number;
}) {
  const from = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);
  const to = useMemo(() => format(addMinutes(new Date(), WINDOW_DAYS * 24 * 60), 'yyyy-MM-dd'), []);

  const { data: available, isLoading: loadingAvailable } = useAvailableSlots({
    serviceId: service.id,
    from,
    to,
  });
  const { data: existingSlots = [], isLoading: loadingExisting } = useServiceSlots(service.id);
  const createSlot = useCreateSlot(service.id);
  const deleteSlot = useDeleteSlot(service.id);

  const slots = available?.slots ?? [];

  const findExistingBlock = (slot: AvailableSlot) =>
    existingSlots.find((s) => s.start === slot.start && s.end === slot.end);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground">
          Buffer: <span className="font-medium text-foreground">{effectiveBufferMins} min</span> between
          bookings. Greyed slots fall inside that buffer around an existing booking or block.
        </p>
      </div>

      {loadingAvailable || loadingExisting ? (
        <div className="text-center py-10 text-muted-foreground text-[13px]">Loading slots…</div>
      ) : slots.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground text-[13px] border border-dashed border-[#D9D9D9] rounded-[12px]">
          No computed slots in the next {WINDOW_DAYS} days.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[360px] overflow-y-auto pr-1">
          {slots.map((slot) => {
            const blocked = isBufferBlocked(slot, existingSlots, effectiveBufferMins);
            const manualBlock = findExistingBlock(slot);
            const disabled = blocked || !slot.available;

            return (
              <button
                key={`${slot.start}-${slot.end}`}
                type="button"
                disabled={disabled && !manualBlock}
                onClick={() => {
                  if (manualBlock) {
                    deleteSlot.mutate(manualBlock.id);
                  } else if (!disabled) {
                    createSlot.mutate({ serviceId: service.id, start: slot.start, end: slot.end });
                  }
                }}
                className={`rounded-[10px] border px-3 py-2 text-left text-[12px] transition-colors ${
                  manualBlock
                    ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                    : disabled
                      ? 'border-[#E5E7EB] bg-muted text-muted-foreground cursor-not-allowed opacity-60'
                      : 'border-[#D9D9D9] bg-card text-foreground hover:border-foreground'
                }`}
                title={
                  manualBlock
                    ? 'Blocked — click to unblock'
                    : disabled
                      ? 'Within buffer of an existing booking'
                      : 'Click to block this slot'
                }
              >
                <div className="font-medium">{format(new Date(slot.start), 'EEE d MMM')}</div>
                <div>
                  {format(new Date(slot.start), 'HH:mm')}–{format(new Date(slot.end), 'HH:mm')}
                </div>
                {manualBlock && <div className="mt-1 text-[11px] font-semibold">Blocked</div>}
                {!manualBlock && disabled && <div className="mt-1 text-[11px]">Buffer</div>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Free-form scheduling for CUSTOM_ORDER services. Deliberately does not fetch
 * availability or the buffer window, and never disables a start/end combination
 * — custom-order timing is flexible by design (PE-2137 AC).
 */
function CustomOrderTimingPicker({ service }: { service: BookingService }) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const createSlot = useCreateSlot(service.id);

  const handleSave = () => {
    setError('');
    if (!start || !end) {
      setError('Start and end are required');
      return;
    }
    createSlot.mutate(
      {
        serviceId: service.id,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        reason: reason.trim() || undefined,
      },
      { onSuccess: () => { setStart(''); setEnd(''); setReason(''); } },
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-[13px] text-muted-foreground">
        Custom-order timing is flexible — pick any start/end, even one that overlaps another booking
        or falls inside a buffer window. No availability check is applied.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Start"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
        <Input
          label="End"
          type="datetime-local"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
        />
      </div>
      <Input
        label="Note (optional)"
        placeholder="e.g. Client fitting, delivery window"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {error && <p className="text-sm text-red-500">{error}</p>}
      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          className="h-[40px]"
          onClick={handleSave}
          loading={createSlot.isPending}
        >
          Save time
        </Button>
      </div>
    </div>
  );
}
